import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import type { Catalog, NestedMessages } from "@/i18n/catalog";

import { defaultLocale, type Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** Five minutes. Also the number the admin page states on screen — see below. */
const STALE_MS = 5 * 60 * 1000;

export interface TranslationOverride {
  locale: string;
  namespace: string;
  key: string;
  value: string;
  source: "edited" | "claude";
  base_en: string | null;
  updated_at: string;
}

/**
 * What a caller wants to see. The runtime merges only hand-written corrections; the admin table
 * shows every row including whatever a bulk import wrote (POS2-1134).
 */
export type OverrideScope = "edited" | "all";

export function overridesQueryKey(locale: string, scope: OverrideScope) {
  return ["i18n-overrides", locale, scope];
}

/** Shape a flat override list into a catalogue the resolver can walk, same as a bundled one. */
export function overridesToCatalog(overrides: readonly TranslationOverride[]): Catalog {
  const catalog: Catalog = {};
  for (const override of overrides) {
    // `key` may be dotted (`group.nested`), so build the same nesting a bundle has rather than
    // storing a literal "group.nested" property the dot-path walk would never reach.
    let node: NestedMessages = (catalog[override.namespace] ??= {});
    const path = override.key.split(".");
    for (const segment of path.slice(0, -1)) {
      const next = node[segment];
      node = typeof next === "object" && next !== null ? next : (node[segment] = {});
    }
    node[path[path.length - 1]] = override.value;
  }
  return catalog;
}

/**
 * The database override layer — the top rung of the fallback chain (POS2-1133).
 *
 * Three properties this hook has to keep, because the whole design rests on them:
 *
 * 1. **It can fail.** A rejected fetch, a 500, or being offline leaves `data` undefined and the
 *    provider renders the bundled catalogue. `retry: false` and no `throwOnError`, so a broken
 *    endpoint costs a correction, never a screen.
 * 2. **It is not the source of truth.** Zero rows is the normal state; every key it carries
 *    already has a bundled value underneath it.
 * 3. **It is deliberately stale.** Five minutes, stated on the admin page, because the admin who
 *    saved gets an immediate cache invalidation and everyone else does not need a websocket to
 *    read a corrected word. The service worker neither precaches nor runtime-caches this response
 *    — see the note in `vite.config.ts`.
 *
 * English is skipped entirely: it is the source language, the thing overrides are written
 * *against*, and correcting English means editing the catalogue in a commit.
 *
 * 4. **It asks for `edited` rows only.** Property 2 above says zero rows is the normal state, and
 *    that is true of today's data rather than of the schema: `source = 'claude'` is in the table's
 *    CHECK and POS2-1150 is the import path that would bulk-write it. Scoping the runtime read is
 *    what keeps that import out of every user's per-load payload (POS2-1134). The scope is part of
 *    the query key, so the admin table's unfiltered fetch cannot share this cache entry.
 */
export function useI18nOverrides(locale: Locale, scope: OverrideScope = "edited") {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const hasToken = !!headers.Authorization.replace("Bearer ", "");

  return useQuery<TranslationOverride[]>({
    queryKey: overridesQueryKey(locale, scope),
    enabled: hasToken && locale !== defaultLocale,
    staleTime: STALE_MS,
    retry: false,
    queryFn: async () => {
      const params = new URLSearchParams({ locale });
      if (scope === "edited") params.set("source", "edited");
      const res = await fetch(`${API_URL}/api/v1/i18n/overrides?${params}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      const body = (await res.json()) as { data: TranslationOverride[] };
      return body.data;
    },
  });
}
